/**
 * THALIA Icon Component
 * Renders THALIA's avatar icon with optional effects
 */

import { motion } from 'framer-motion';

interface THALIAIconProps {
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
  animate?: boolean;
  glowColor?: string;
}

const SIZES = {
  xs: 'w-4 h-4',
  sm: 'w-6 h-6',
  md: 'w-8 h-8',
  lg: 'w-10 h-10',
  xl: 'w-12 h-12',
};

export default function THALIAIcon({ 
  size = 'md', 
  className = '',
  animate = false,
  glowColor = 'rgba(0, 255, 128, 0.5)'
}: THALIAIconProps) {
  const sizeClass = SIZES[size];
  
  if (animate) {
    return (
      <div className={`relative ${className}`}>
        <motion.img
          src="/thalia/thalia.jpg"
          alt="THALIA"
          className={`${sizeClass} rounded-full object-cover`}
          animate={{ 
            filter: [
              'drop-shadow(0 0 4px rgba(0, 255, 128, 0.5))',
              'drop-shadow(0 0 8px rgba(0, 255, 128, 0.8))',
              'drop-shadow(0 0 4px rgba(0, 255, 128, 0.5))'
            ]
          }}
          transition={{ duration: 2, repeat: Infinity }}
        />
        {/* Scanning ring effect */}
        <motion.div
          className="absolute inset-0 rounded-full border border-green-500/50"
          animate={{ scale: [1, 1.3, 1], opacity: [0.5, 0, 0.5] }}
          transition={{ duration: 2, repeat: Infinity }}
        />
      </div>
    );
  }
  
  return (
    <img
      src="/thalia/thalia.jpg"
      alt="THALIA"
      className={`${sizeClass} rounded-full object-cover ${className}`}
      style={{ filter: `drop-shadow(0 0 4px ${glowColor})` }}
    />
  );
}

// Inline version for text contexts (fallback to emoji if image fails)
export function THALIAInline({ className = '' }: { className?: string }) {
  return (
    <img
      src="/thalia/thalia.jpg"
      alt="THALIA"
      className={`inline-block w-5 h-5 rounded-full align-middle ${className}`}
      style={{ filter: 'drop-shadow(0 0 3px rgba(0, 255, 128, 0.5))' }}
      onError={(e) => {
        // Fallback to emoji if image fails to load
        const target = e.target as HTMLImageElement;
        target.style.display = 'none';
        const span = document.createElement('span');
        span.textContent = '🤖';
        target.parentNode?.insertBefore(span, target);
      }}
    />
  );
}
